import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import {
  Search, Download, Mail, Edit, Trash2, CheckCircle2, AlertTriangle, Plus, Receipt, ExternalLink,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import NewInvoiceModal from "@/components/NewInvoiceModal";
import KpiCard, { KpiGrid } from "@/components/KpiCard";
import RowActions from "@/components/RowActions";
import EmptyState from "@/components/EmptyState";
import { formatMoney, formatDate } from "@/lib/format";

const STATUSES = ["all", "draft", "sent", "approved", "revision_requested", "denied", "paid", "overdue"];

const STATUS_PILL_CLASSES = {
  paid: "bg-emerald-600 text-white",
  overdue: "bg-red-100 text-red-700",
  void: "bg-slate-200 text-slate-600",
  draft: "bg-slate-100 text-slate-600",
  sent: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  revision_requested: "bg-sky-100 text-sky-700",
  denied: "bg-red-100 text-red-700",
};

function InvStatusPill({ inv }) {
  const label = inv.is_overdue && inv.status !== "paid" ? "overdue" : inv.status;
  const cls = STATUS_PILL_CLASSES[label] || "bg-amber-100 text-amber-700";
  return (
    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
  );
}

export default function AdminPayments() {
  const nav = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, []);

  const filtered = invoices.filter((inv) => {
    if (statusFilter === "overdue") {
      if (!inv.is_overdue) return false;
    } else if (statusFilter !== "all" && inv.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (inv.invoice_id || "").toLowerCase().includes(q) ||
        (inv.title || "").toLowerCase().includes(q) ||
        (inv.customer_code || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const totals = filtered.reduce((acc, i) => {
    if (i.status === "paid") acc.paid += i.amount || 0;
    else acc.outstanding += i.amount || 0;
    if (i.is_overdue) acc.overdue += i.amount || 0;
    return acc;
  }, { paid: 0, outstanding: 0, overdue: 0 });

  const downloadCsv = async () => {
    const r = await api.get("/admin/invoices.csv", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `powerbuilt_invoices_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const remove = async (inv) => {
    if (!confirm(`Delete invoice ${inv.invoice_id}? Cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.invoice_id}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-payments">
      <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
        <div>
          <p className="overline">Money in</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Invoices & Payments</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={downloadCsv} data-testid="export-csv" className="btn-outline-navy text-sm py-2 px-3">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </button>
          <button
            onClick={() => setCreating(true)}
            data-testid="new-invoice-btn"
            className="btn-accent text-sm py-2 px-4 inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> New invoice
          </button>
        </div>
      </div>

      <NewInvoiceModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => load()}
      />

      {/* KPI strip */}
      <KpiGrid className="grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 mb-6">
        <KpiCard testId="tile-paid" label="Paid" value={totals.paid} money decimals={0}
          tone={totals.paid > 0 ? "positive" : "neutral"} icon={CheckCircle2} />
        <KpiCard testId="tile-outstanding" label="Outstanding" value={totals.outstanding} money decimals={0}
          tone={totals.outstanding > 0 ? "attention" : "positive"} icon={Receipt} />
        <KpiCard testId="tile-overdue" label="Overdue" value={totals.overdue} money decimals={0}
          tone={totals.overdue > 0 ? "critical" : "positive"} icon={AlertTriangle} />
      </KpiGrid>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by invoice #, title, customer code…"
            data-testid="invoice-search"
            className="w-full pl-9 pr-3 py-2 border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md text-sm"
          />
        </div>
        <div className="flex gap-1 bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 rounded-md p-1 overflow-x-auto no-scrollbar">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
                    className={`text-xs px-3 py-1 rounded whitespace-nowrap transition ${
                      statusFilter === s ? "bg-[#0A2540] text-white" : "text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"
                    }`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="card-pb overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-700">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Invoice</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Customer</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Title</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Due</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Status</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase text-[#475569] dark:text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((inv) => (
              <tr key={inv.invoice_id} data-testid={`admin-inv-${inv.invoice_id}`}
                  className={`border-b border-[#E2E8F0] dark:border-slate-700 ${inv.is_overdue ? "bg-red-50 dark:bg-red-900/10" : ""}`}>
                <td className="px-4 py-3 font-mono text-xs">
                  <Link to={`/admin/invoices/${inv.invoice_id}`} className="text-[#C9A356] hover:underline" data-testid={`open-${inv.invoice_id}`}>
                    {inv.invoice_id}
                  </Link>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-[#C9A356]">{inv.customer_code}</td>
                <td className="px-4 py-3 text-sm">{inv.title}</td>
                <td className="px-4 py-3 text-right font-medium tabular-nums">{formatMoney(inv.amount)}</td>
                <td className="px-4 py-3 text-xs">
                  {inv.due_date ? (
                    <span className={inv.is_overdue ? "text-red-600 font-bold" : ""}>
                      {inv.is_overdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                      {formatDate(inv.due_date)}
                    </span>
                  ) : <span className="text-[#94A3B8]">—</span>}
                </td>
                <td className="px-4 py-3">
                  <InvStatusPill inv={inv} />
                </td>
                <td className="px-4 py-3">
                  <RowActions
                    testId={`inv-actions-${inv.invoice_id}`}
                    primary={{
                      label: "Open",
                      icon: ExternalLink,
                      onClick: () => nav(`/admin/invoices/${inv.invoice_id}`),
                      testId: `detail-${inv.invoice_id}`,
                    }}
                    items={[
                      ...(inv.status !== "paid" ? [{
                        label: "Mark as paid", icon: CheckCircle2,
                        onClick: () => setPaying(inv),
                        testId: `mark-paid-${inv.invoice_id}`,
                      }] : []),
                      ...(inv.status !== "paid" ? [{
                        label: "Send reminder", icon: Mail,
                        onClick: () => setReminder(inv),
                        testId: `remind-${inv.invoice_id}`,
                      }] : []),
                      {
                        label: "Edit invoice", icon: Edit,
                        onClick: () => setEditing(inv),
                        testId: `edit-${inv.invoice_id}`,
                      },
                      {
                        label: "Delete", icon: Trash2, danger: true,
                        onClick: () => remove(inv),
                        disabled: inv.status === "paid",
                        testId: `del-${inv.invoice_id}`,
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="px-5 py-16">
                <EmptyState
                  icon={Receipt}
                  title={invoices.length === 0 ? "No invoices yet" : "No invoices match this filter"}
                  body={invoices.length === 0
                    ? "Send a quote or create one from any client profile — it lands here for tracking."
                    : "Try clearing the search or picking a different status."}
                  action={invoices.length === 0 ? {
                    label: "New invoice",
                    onClick: () => setCreating(true),
                    testId: "empty-new-invoice",
                  } : null}
                />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && <EditInvoiceModal invoice={editing} onClose={() => { setEditing(null); load(); }} />}
      {paying && <MarkPaidModal invoice={paying} onClose={() => { setPaying(null); load(); }} />}
      {reminder && <ReminderModal invoice={reminder} onClose={() => setReminder(null)} />}
    </div>
  );
}

function EditInvoiceModal({ invoice, onClose }) {
  const [form, setForm] = useState({
    title: invoice.title || "",
    amount: invoice.amount || 0,
    due_date: invoice.due_date || "",
    discount: invoice.discount || 0,
    status: invoice.status || "open",
    notes: invoice.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/invoices/${invoice.invoice_id}`, {
        ...form, amount: parseFloat(form.amount), discount: parseFloat(form.discount || 0),
      });
      toast.success("Invoice updated");
      onClose();
    } catch {
      toast.error("Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-invoice-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-[#0A2540] dark:text-white">Edit invoice {invoice.invoice_id}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Title">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="inv-title"
                   className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (USD)">
              <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="inv-amount"
                     className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
            </Field>
            <Field label="Discount (USD)">
              <input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })}
                     className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Due date">
              <input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="inv-due"
                     className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="inv-status"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm">
                <option value="draft">Draft</option>
                <option value="open">Open</option>
                <option value="paid">Paid</option>
                <option value="void">Void</option>
              </select>
            </Field>
          </div>
          <Field label="Notes (internal)">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
          </Field>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-accent text-sm" data-testid="save-invoice">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarkPaidModal({ invoice, onClose }) {
  const [method, setMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/invoices/${invoice.invoice_id}/mark-paid`, { method, notes });
      toast.success("Marked paid ✓");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="mark-paid-modal">
        <h3 className="font-heading text-xl text-[#0A2540] dark:text-white mb-1">Mark as paid</h3>
        <p className="text-sm text-[#475569] dark:text-slate-400 mb-4">{invoice.invoice_id} · ${invoice.amount?.toFixed(2)}</p>
        <Field label="Payment method">
          <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="paid-method"
                  className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm">
            <option value="cash">Cash</option>
            <option value="check">Check</option>
            <option value="bank_transfer">Bank transfer</option>
            <option value="wire">Wire</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Notes (optional)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} placeholder="Reference #, deposit slip ID, etc."
                    data-testid="paid-notes"
                    className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
        </Field>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-outline-navy text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-accent text-sm" data-testid="confirm-mark-paid">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm payment
          </button>
        </div>
      </div>
    </div>
  );
}

function ReminderModal({ invoice, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/invoices/${invoice.invoice_id}/reminder`).then((r) => setData(r.data));
  }, [invoice.invoice_id]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="reminder-modal">
        <h3 className="font-heading text-xl text-[#0A2540] dark:text-white mb-1">Send reminder</h3>
        <p className="text-sm text-[#475569] dark:text-slate-400 mb-4">{invoice.invoice_id} · {invoice.customer_code}</p>
        {!data ? <div>Loading…</div> : (
          <>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-3 text-sm text-[#0A2540] dark:text-white whitespace-pre-wrap mb-4">{data.message}</div>
            <div className="flex flex-col gap-2">
              {data.whatsapp_url ? (
                <a href={data.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="reminder-whatsapp"
                   className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-medium text-white">
                  <MessageCircle className="w-4 h-4" /> Send via WhatsApp ({data.client_phone})
                </a>
              ) : (
                <p className="text-xs text-amber-600">Client has no phone on file — only email is available.</p>
              )}
              {data.mailto_url && (
                <a href={data.mailto_url} data-testid="reminder-email"
                   className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium text-white">
                  <Mail className="w-4 h-4" /> Open in email client
                </a>
              )}
              <button onClick={() => { navigator.clipboard.writeText(data.message); toast.success("Copied"); }} className="btn-outline-navy text-sm">
                Copy message
              </button>
            </div>
          </>
        )}
        <button onClick={onClose} className="btn-outline-navy text-sm w-full mt-4">Close</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 mb-1 font-bold">{label}</label>
      {children}
    </div>
  );
}
